/**
 * commitmentViewCore — P3/P4 of the Decision Propagation & Lineage PRD.
 *
 * PRD: docs/9-exploration/DECISION-PROPAGATION-AND-LINEAGE-PRD.md §5–§6.
 *
 * The problem this exists to solve (Amato/Pandit's leakage chain): a decision
 * is made correctly, leadership understands X, a division interprets X′, a
 * department translates X″, and the team executes X‴. Patton's version is
 * blunter — ask the team executing something why, and you get a guess. The
 * pieces of the answer are all recorded already; they are just scattered
 * across seven surfaces. This module assembles them into one object.
 *
 * PURE AND READ-ONLY. No fetching (commitmentViewService.ts), no writes
 * anywhere — assembly only ever reads append-only records (PRD non-goals).
 *
 * THE DESIGN CONSTRAINT: a section with nothing in it must never render as a
 * blank. The report only earns trust if "we don't know why" is visibly
 * different from "nobody has looked". So every section carries an explicit
 * `absence` when it is empty, and `pattonTest()` reports readiness from the
 * sections that actually answer rather than from a count of rows fetched.
 */

import {
  narrateAlternatives,
  coverageOf,
  type AlternativesCoverage,
  type RejectedAlternative,
} from './rejectedAlternatives';

// ── Identity ────────────────────────────────────────────────────────────────

/**
 * A commitment's durable identity: the (plan cycle, item) pair.
 *
 * This is the identity migration 680 already chose for commitment terms
 * ("durable commitment identity across plan versions, surviving plan_version
 * supersession") and 681 for resource claims. Reusing it is what lets the view
 * survive supersession: plan versions come and go beneath the pair, and the
 * URL keeps resolving.
 */
export interface CommitmentIdentity {
  planCycleId: string;
  itemId: string;
}

export function commitmentKey(id: CommitmentIdentity): string {
  return `${id.planCycleId}:${id.itemId}`;
}

/**
 * Parses the composite id used by the route and by `/v1/commitments/:id`.
 *
 * Returns null rather than a half-parsed pair: a malformed id must 404, not
 * silently resolve to some other commitment.
 */
export function parseCommitmentKey(raw: string | null | undefined): CommitmentIdentity | null {
  if (!raw) return null;
  const parts = raw.split(':');
  if (parts.length !== 2) return null;
  const [planCycleId, itemId] = parts.map((p) => p.trim());
  if (!planCycleId || !itemId) return null;
  return { planCycleId, itemId };
}

// ── P4: staleness ───────────────────────────────────────────────────────────

/**
 * What a surface quoting this commitment is actually quoting.
 *
 * `unauthorized` is the verdict most easily got wrong. A surface quoting a
 * plan version NEWER than the authorized one is not stale — it is ahead of
 * authority, quoting something nobody has committed to. Collapsing that into
 * "current" is how a draft gets executed.
 */
export type StalenessVerdict =
  | 'current'
  | 'stale'
  | 'unauthorized'
  | 'no_authorized_version'
  | 'unknown';

export interface StalenessReading {
  verdict: StalenessVerdict;
  /** True for anything a reader must be warned about. */
  isDrift: boolean;
  label: string;
  detail: string;
}

export function judgeStaleness(args: {
  /** The version number the quoting surface is showing. */
  quotedVersionNumber: number | null | undefined;
  /** The version number of the cycle's active (unsuperseded) authorization. */
  authoritativeVersionNumber: number | null | undefined;
}): StalenessReading {
  const quoted = args.quotedVersionNumber;
  const authoritative = args.authoritativeVersionNumber;

  if (authoritative === null || authoritative === undefined) {
    return {
      verdict: 'no_authorized_version',
      isDrift: true,
      label: 'Not authorized',
      detail:
        'This plan cycle has no authorized Plan of Record, so nothing here is a commitment yet — it is a draft being quoted.',
    };
  }
  if (quoted === null || quoted === undefined) {
    return {
      verdict: 'unknown',
      isDrift: true,
      label: 'Version unknown',
      detail: `The authoritative version is v${authoritative}, but this surface does not record which version it is quoting.`,
    };
  }
  if (quoted === authoritative) {
    return {
      verdict: 'current',
      isDrift: false,
      label: `v${quoted}`,
      detail: `Quoting the authorized version (v${authoritative}).`,
    };
  }
  if (quoted < authoritative) {
    return {
      verdict: 'stale',
      isDrift: true,
      label: `v${quoted} — superseded`,
      detail: `This quotes v${quoted}; the authorized version is v${authoritative}. Check the change history before acting on it.`,
    };
  }
  return {
    verdict: 'unauthorized',
    isDrift: true,
    label: `v${quoted} — not authorized`,
    detail: `This quotes v${quoted}, which is ahead of the authorized v${authoritative}. It is a draft, not a commitment.`,
  };
}

// ── Section model ───────────────────────────────────────────────────────────

export type SectionKey =
  | 'what'
  | 'why'
  | 'instead_of_what'
  | 'at_what_cost'
  | 'under_what_conditions'
  | 'how_it_has_changed'
  | 'who';

export const SECTION_QUESTIONS: Record<SectionKey, string> = {
  what: 'What did we commit to?',
  why: 'Why did we commit to it?',
  instead_of_what: 'Instead of what?',
  at_what_cost: 'At what cost?',
  under_what_conditions: 'Under what conditions?',
  how_it_has_changed: 'How has it changed?',
  who: 'Who decided?',
};

/**
 * A section either answers its question or explains why it cannot.
 *
 * `absence` is not an error string. Most absences are legitimate — a
 * commitment with no resource claims declared has an honest empty cost
 * section — and saying so is more useful than a blank panel that looks broken.
 */
export interface SectionState {
  key: SectionKey;
  answered: boolean;
  absence: string | null;
}

// ── Inputs ──────────────────────────────────────────────────────────────────

export interface CommitmentItemFacts {
  itemId: string;
  title: string | null;
  description: string | null;
}

/** One plan version's view of this commitment (an `initiatives` row). */
export interface CommitmentVersionState {
  planVersionId: string;
  versionNumber: number;
  createdAt: string | null;
  status: string;
  priorityPosition: number | null;
  certaintyLevel: string | null;
  dropReason: string | null;
  /** plan_versions.change_reason */
  changeReason: string | null;
  /** plan_versions.source_decision_id */
  sourceDecisionId: string | null;
}

export interface CommitmentDecisionFacts {
  decisionId: string;
  title: string;
  decidedAt: string | null;
  decidedByName: string | null;
  rationaleContext: string | null;
  rationaleReasoning: string | null;
  rationaleAlternatives: string | null;
  reconsiderationConditions: string | null;
  sessionId: string | null;
}

export interface CommitmentTermFacts {
  id: string;
  kind: string;
  conditionText: string;
  status: string;
  reviewBy: string | null;
}

export interface CommitmentClaimFacts {
  id: string;
  claimKind: string;
  resourceType: string | null;
  declaredHours: number | null;
  declaredAmount: number | null;
  currency: string | null;
  status: string;
}

export interface CommitmentAuthorityFacts {
  /** plan_of_record_snapshots row for the cycle's active authorization. */
  authorizedVersionNumber: number | null;
  authorizedAt: string | null;
  authorizedBy: string | null;
  /** por_record_snapshots.source_cycle_report_id (mig 698 / P1). */
  sourceCycleReportId: string | null;
  /** planning_audit_events.payload.source_cycle_report_basis (mig 698 / P1). */
  sourceCycleReportBasis: string | null;
}

export interface CommitmentViewInput {
  identity: CommitmentIdentity;
  cycleName: string | null;
  item: CommitmentItemFacts | null;
  versions: CommitmentVersionState[];
  decisions: CommitmentDecisionFacts[];
  alternatives: RejectedAlternative[];
  terms: CommitmentTermFacts[];
  claims: CommitmentClaimFacts[];
  authority: CommitmentAuthorityFacts;
}

// ── Assembly ────────────────────────────────────────────────────────────────

export interface CommitmentView {
  identity: CommitmentIdentity;
  key: string;
  cycleName: string | null;
  item: CommitmentItemFacts | null;

  /** The commitment's state in the latest plan version that carries it. */
  currentState: CommitmentVersionState | null;
  /** Its state in the authorized version, which may be older. */
  authorizedState: CommitmentVersionState | null;
  staleness: StalenessReading;

  /** Version states where something actually changed, newest first. */
  changeHistory: CommitmentVersionState[];

  decisions: CommitmentDecisionFacts[];
  alternatives: RejectedAlternative[];
  alternativesNarration: string;
  alternativesCoverage: AlternativesCoverage;

  terms: CommitmentTermFacts[];
  claims: CommitmentClaimFacts[];
  authority: CommitmentAuthorityFacts;

  sections: Record<SectionKey, SectionState>;
  exists: boolean;
}

function section(key: SectionKey, answered: boolean, absence: string): SectionState {
  return { key, answered, absence: answered ? null : absence };
}

/**
 * Which versions represent a real change to THIS commitment.
 *
 * A plan cycle re-versions for reasons that have nothing to do with any one
 * item, so listing every version as a change to this commitment would bury the
 * two that mattered. A version counts when the commitment's status, position,
 * certainty or drop reason differs from the previous version — or when it
 * first appears, which is the commitment being made.
 */
export function changeHistoryFor(versions: CommitmentVersionState[]): CommitmentVersionState[] {
  const ordered = [...versions].sort((a, b) => a.versionNumber - b.versionNumber);
  const changed: CommitmentVersionState[] = [];

  for (let i = 0; i < ordered.length; i += 1) {
    const current = ordered[i];
    const previous = ordered[i - 1];
    if (
      !previous ||
      previous.status !== current.status ||
      previous.priorityPosition !== current.priorityPosition ||
      previous.certaintyLevel !== current.certaintyLevel ||
      previous.dropReason !== current.dropReason
    ) {
      changed.push(current);
    }
  }
  return changed.reverse();
}

export function assembleCommitmentView(input: CommitmentViewInput): CommitmentView {
  const ordered = [...input.versions].sort((a, b) => a.versionNumber - b.versionNumber);
  const currentState = ordered.length > 0 ? ordered[ordered.length - 1] : null;
  const authorizedState =
    input.authority.authorizedVersionNumber === null
      ? null
      : (ordered.find((v) => v.versionNumber === input.authority.authorizedVersionNumber) ?? null);

  const staleness = judgeStaleness({
    quotedVersionNumber: currentState?.versionNumber ?? null,
    authoritativeVersionNumber: input.authority.authorizedVersionNumber,
  });

  const withRationale = input.decisions.filter(
    (d) => Boolean(d.rationaleContext?.trim()) || Boolean(d.rationaleReasoning?.trim()),
  );
  const prose = input.decisions.map((d) => d.rationaleAlternatives).find((p) => p?.trim()) ?? null;

  const sections: Record<SectionKey, SectionState> = {
    what: section(
      'what',
      Boolean(input.item) && ordered.length > 0,
      input.item
        ? 'This item is not carried by any plan version in this cycle — there is no commitment here to read.'
        : 'The item behind this commitment could not be found.',
    ),
    why: section(
      'why',
      withRationale.length > 0,
      input.decisions.length > 0
        ? 'A decision is linked, but no one recorded its context or reasoning. This is the gap that makes the "why" unanswerable two levels down.'
        : 'No decision record is linked to this commitment, so the reasoning behind it was never memorialized.',
    ),
    instead_of_what: section(
      'instead_of_what',
      input.alternatives.length > 0 || Boolean(prose?.trim()),
      'Nothing was recorded about what this was chosen over.',
    ),
    at_what_cost: section(
      'at_what_cost',
      input.claims.length > 0,
      'No resources were declared against this commitment, so what it costs is not on the record.',
    ),
    under_what_conditions: section(
      'under_what_conditions',
      input.terms.length > 0,
      'No stopping rules are attached, so nothing says when this commitment would have to justify itself again.',
    ),
    how_it_has_changed: section(
      'how_it_has_changed',
      ordered.length > 0,
      'This commitment appears in no plan version, so it has no history.',
    ),
    who: section(
      'who',
      Boolean(input.authority.authorizedBy) || input.decisions.some((d) => d.decidedByName),
      'Neither an authorizer nor a named decider is recorded against this commitment.',
    ),
  };

  return {
    identity: input.identity,
    key: commitmentKey(input.identity),
    cycleName: input.cycleName,
    item: input.item,
    currentState,
    authorizedState,
    staleness,
    changeHistory: changeHistoryFor(ordered),
    decisions: input.decisions,
    alternatives: input.alternatives,
    alternativesNarration: narrateAlternatives(input.alternatives),
    alternativesCoverage: coverageOf({ structuralCount: input.alternatives.length, prose }),
    terms: input.terms,
    claims: input.claims,
    authority: input.authority,
    sections,
    exists: Boolean(input.item) && ordered.length > 0,
  };
}

// ── The Patton test ─────────────────────────────────────────────────────────

/**
 * The PRD's success metric, computed rather than asserted: can someone two
 * levels from the decision answer "why" from this view alone?
 *
 * `why` is not one section among seven. A view with cost, conditions, history
 * and participants but no recorded reasoning fails the test outright — that is
 * exactly the "you'll get a guess" state Patton described, and averaging it
 * away into "6 of 7 sections answered" would report the failure as a pass.
 */
export interface PattonReading {
  passes: boolean;
  answered: SectionKey[];
  unanswered: SectionKey[];
  summary: string;
}

export function pattonTest(view: CommitmentView): PattonReading {
  const keys = Object.keys(view.sections) as SectionKey[];
  const answered = keys.filter((k) => view.sections[k].answered);
  const unanswered = keys.filter((k) => !view.sections[k].answered);
  const passes = view.sections.why.answered && view.sections.what.answered;

  const summary = passes
    ? `Answers ${answered.length} of ${keys.length} questions, including why.`
    : view.sections.what.answered
      ? `Cannot answer "why" from the record — ${answered.length} of ${keys.length} other questions are answered, but the reasoning was never written down.`
      : 'There is no commitment here to read.';

  return { passes, answered, unanswered, summary };
}
